import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import BookingModal, { type BookingModalInitial } from './BookingModal';

interface BookingModalValue {
  openBooking: (initial?: BookingModalInitial) => void;
  draft: BookingModalInitial;
  setDraft: (patch: Partial<BookingModalInitial>) => void;
}

const BookingModalContext = createContext<BookingModalValue>({
  openBooking: () => {},
  draft: {},
  setDraft: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export function useBookingModal() {
  return useContext(BookingModalContext);
}

export function BookingModalProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [initial, setInitial] = useState<BookingModalInitial | undefined>();
  // Mirrors whatever the guest has typed into the hero "Check availability"
  // widget, even before they submit it — so a "Book Now" button clicked
  // elsewhere on the page (nav header, CTA sections, etc.) opens the modal
  // pre-filled with those same values instead of a blank form.
  const [draft, setDraftState] = useState<BookingModalInitial>({});
  const setDraft = useCallback((patch: Partial<BookingModalInitial>) => {
    setDraftState((prev) => ({ ...prev, ...patch }));
  }, []);
  const openBooking = useCallback(
    (values?: BookingModalInitial) => {
      setInitial(values ?? draft);
      setOpen(true);
    },
    [draft],
  );
  const closeBooking = useCallback(() => setOpen(false), []);

  // Lets external links (ads, emails, social posts) deep-link straight into
  // the booking flow via e.g. homelandstay.com/?book=1 — mounted once here
  // so it fires no matter which page under Layout the link lands on. The
  // param is stripped right after so refreshing or sharing the resulting
  // URL doesn't re-trigger it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('book')) {
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('book');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <BookingModalContext.Provider value={{ openBooking, draft, setDraft }}>
      {children}
      {open && <BookingModal onClose={closeBooking} initial={initial} onDraftChange={setDraft} />}
    </BookingModalContext.Provider>
  );
}
